// CR #208 — Plan → 3D extrusion. Pure module: takes the drafting suite's plan
// JSON (world-space INCHES, SVG orientation: x right, y down) and produces the
// passive model for SketchEngine3D as EngineObject[] in Z-UP FEET (the same
// frame .mmdl geometry arrives in; the engine converts to three's Y-up).
//
// Mapping: model X = plan x/12 · model Y = −plan y/12 · model Z = height/12.
// After the engine's (x, y, z) → (x, z, −y) conversion the plan reads
// naturally: plan-north (up on the drawing) is away from the Front camera.
//
// What gets built (v1 massing, defaults stated honestly in the UI/changelog):
//  - Walls: boxes along each segment at true thickness × the level's wall
//    height (default 8'0"). Openings are PUNCHED without CSG by splitting the
//    wall run into sub-boxes: full-height piece between openings, header over
//    doors/cased openings (head 6'8"), sill + header around windows (sill
//    3'0", height 4'0" → head 7'0"). Kind-based defaults, per-opening sizes
//    can come later.
//  - Levels stack: level i sits on Σ prior (wall height + 12" floor depth);
//    levels above the first get a 12" floor slab over their wall bbox.
//  - Roof (optional, per level): 'gable' = two 6"-thick sloped slabs over the
//    level's wall bbox at a rise-per-12 pitch, ridge along the longest bbox
//    axis, 16" overhang all around, gable ends open (massing only — no hips,
//    no valleys, no per-wall eave detection); or 'flat' = one 6" slab with
//    the same overhang.
//  - Site: existing structures = plain 10'-tall boxes; trees = a low-poly
//    trunk + canopy sphere. Both sit at ground (z 0) whatever level they were
//    drawn on.

import type { EngineObject } from '@/components/common/SketchEngine3D';

export interface PlanWall { id: number; x1: number; y1: number; x2: number; y2: number; thick: number }
export interface PlanOpening { id: number; wallId: number; offset: number; width: number; kind: 'door' | 'window' | 'opening'; tag: string }
export interface PlanStructure { id: number; x: number; y: number; w: number; h: number; label: string }
export interface PlanTree { id: number; x: number; y: number; canopy: number }
export interface RoofSetting { kind: 'none' | 'gable' | 'flat'; pitch: number } // pitch = rise per 12
export interface PlanLevel3D {
  name: string;
  walls: PlanWall[];
  openings: PlanOpening[];
  structures?: PlanStructure[];
  trees?: PlanTree[];
  heightIn?: number;      // wall height, inches (default 96)
  roof?: RoofSetting;     // default none
}

export const DEFAULT_WALL_HEIGHT_IN = 96;   // 8'0"
export const DOOR_HEAD_IN = 80;             // 6'8"
export const WINDOW_SILL_IN = 36;           // 3'0"
export const WINDOW_HEIGHT_IN = 48;         // 4'0"
export const FLOOR_THICK_IN = 12;           // 1'0" floor depth between levels
export const ROOF_OVERHANG_IN = 16;
export const ROOF_SLAB_IN = 6;              // massing-roof slab thickness (vertical)
export const STRUCTURE_HEIGHT_IN = 120;     // existing structures: 10'

type RGBA = [number, number, number, number];
const WALL_COLOR: RGBA = [211, 211, 211, 255];
const FLOOR_COLOR: RGBA = [222, 205, 175, 255];
const ROOF_COLOR: RGBA = [176, 116, 73, 255];
const STRUCT_COLOR: RGBA = [154, 154, 154, 255];
const TRUNK_COLOR: RGBA = [122, 85, 58, 255];
const CANOPY_COLOR: RGBA = [92, 127, 79, 255];

interface MeshAcc { positions: number[]; triangles: number[] }

/** plan inches (px, py) + height inches → model feet [x, y, z] (Z up). */
const P = (px: number, py: number, zIn: number): [number, number, number] => [px / 12, -py / 12, zIn / 12];

/** Hexahedron from 8 verts (bottom 0-3 then top 4-7, matching order). */
function pushHexa(acc: MeshAcc, v: [number, number, number][]) {
  const base = acc.positions.length / 3;
  for (const p of v) acc.positions.push(p[0], p[1], p[2]);
  const quad = (a: number, b: number, c: number, d: number) =>
    acc.triangles.push(base + a, base + b, base + c, base + a, base + c, base + d);
  quad(0, 3, 2, 1); // bottom
  quad(4, 5, 6, 7); // top
  for (let k = 0; k < 4; k++) {
    const k2 = (k + 1) % 4;
    quad(k, k2, 4 + k2, 4 + k); // sides
  }
}

/** Axis-aligned-in-plan box: plan rect corners (4, CCW) between two heights. */
function pushPrism(acc: MeshAcc, corners: [number, number][], z0In: number, z1In: number) {
  pushHexa(acc, [
    ...corners.map(([x, y]) => P(x, y, z0In)),
    ...corners.map(([x, y]) => P(x, y, z1In)),
  ]);
}

const obj = (type: string, name: string, color: RGBA, acc: MeshAcc): EngineObject =>
  ({ type, name, meshes: [{ color, positions: acc.positions, triangles: acc.triangles }] });

const ftLabel = (inches: number): string => {
  const ft = Math.floor(inches / 12), rem = Math.round(inches - ft * 12);
  return rem ? `${ft}'${rem}"` : `${ft}'`;
};

function wallBbox(walls: PlanWall[]): { x0: number; y0: number; x1: number; y1: number } | null {
  if (walls.length === 0) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const w of walls) {
    x0 = Math.min(x0, w.x1, w.x2); x1 = Math.max(x1, w.x1, w.x2);
    y0 = Math.min(y0, w.y1, w.y2); y1 = Math.max(y1, w.y1, w.y2);
  }
  return { x0, y0, x1, y1 };
}

/** One wall with its openings punched, as sub-boxes. baseZ/topZ inches. */
function extrudeWall(level: PlanLevel3D, w: PlanWall, baseZIn: number, topZIn: number): EngineObject | null {
  const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
  if (len < 1 || topZIn - baseZIn < 1) return null;
  const H = topZIn - baseZIn;
  const ux = (w.x2 - w.x1) / len, uy = (w.y2 - w.y1) / len;
  const nx = -uy * (w.thick / 2), ny = ux * (w.thick / 2);
  const acc: MeshAcc = { positions: [], triangles: [] };
  // Sub-box spanning [s0, s1] along the run and [z0, z1] of the height —
  // both relative to the wall base.
  const piece = (s0: number, s1: number, z0: number, z1: number) => {
    if (s1 - s0 < 0.25 || z1 - z0 < 0.25) return;
    const ax = w.x1 + ux * s0, ay = w.y1 + uy * s0;
    const bx = w.x1 + ux * s1, by = w.y1 + uy * s1;
    pushPrism(acc, [
      [ax + nx, ay + ny], [bx + nx, by + ny], [bx - nx, by - ny], [ax - nx, ay - ny],
    ], baseZIn + z0, baseZIn + z1);
  };
  const ops = level.openings
    .filter(o => o.wallId === w.id)
    .map(o => ({ ...o, offset: Math.max(0, Math.min(len, o.offset)), width: Math.min(o.width, len - Math.max(0, Math.min(len, o.offset))) }))
    .filter(o => o.width > 1)
    .sort((a, b) => a.offset - b.offset);
  let pos = 0;
  for (const o of ops) {
    if (o.offset > pos) piece(pos, o.offset, 0, H);
    const a = o.offset, b = o.offset + o.width;
    if (o.kind === 'window') {
      const sill = Math.min(WINDOW_SILL_IN, H);
      const head = Math.min(WINDOW_SILL_IN + WINDOW_HEIGHT_IN, H);
      piece(a, b, 0, sill);           // below the sill
      if (head < H) piece(a, b, head, H); // above the head
    } else {
      // door / cased opening: floor to head, header above
      const head = Math.min(DOOR_HEAD_IN, H);
      if (head < H) piece(a, b, head, H);
    }
    pos = Math.max(pos, b);
  }
  if (pos < len) piece(pos, len, 0, H);
  if (acc.positions.length === 0) return null;
  const opsNote = ops.length ? ` · ${ops.length} opening${ops.length === 1 ? '' : 's'}` : '';
  return obj('Wall', `${level.name} — wall ${ftLabel(len)} (${w.thick}" thick)${opsNote}`, WALL_COLOR, acc);
}

function extrudeRoof(level: PlanLevel3D, topZIn: number): EngineObject[] {
  const roof = level.roof;
  if (!roof || roof.kind === 'none') return [];
  const bb = wallBbox(level.walls);
  if (!bb) return [];
  const OV = ROOF_OVERHANG_IN, T = ROOF_SLAB_IN;
  const out: EngineObject[] = [];
  if (roof.kind === 'flat') {
    const acc: MeshAcc = { positions: [], triangles: [] };
    pushPrism(acc, [
      [bb.x0 - OV, bb.y0 - OV], [bb.x1 + OV, bb.y0 - OV], [bb.x1 + OV, bb.y1 + OV], [bb.x0 - OV, bb.y1 + OV],
    ], topZIn, topZIn + T);
    out.push(obj('Roof', `${level.name} — flat roof (16" overhang)`, ROOF_COLOR, acc));
    return out;
  }
  // Gable: ridge along the longest bbox axis; plane hits wall-top at the
  // bbox edge and rises at pitch/12 to the ridge; overhang drops past it.
  const p = Math.max(0.5, Math.min(24, roof.pitch || 4));
  const alongX = (bb.x1 - bb.x0) >= (bb.y1 - bb.y0);
  const zAt = (dist: number, half: number) => topZIn + (half - dist) * (p / 12); // dist from ridge (inches)
  const slope = (name: string, corners: [number, number][], zs: number[]): EngineObject => {
    const acc: MeshAcc = { positions: [], triangles: [] };
    pushHexa(acc, [
      ...corners.map(([x, y], i) => P(x, y, zs[i])),
      ...corners.map(([x, y], i) => P(x, y, zs[i] + T)),
    ]);
    return obj('Roof', name, ROOF_COLOR, acc);
  };
  const label = `${level.name} — gable roof ${p}/12`;
  if (alongX) {
    const midY = (bb.y0 + bb.y1) / 2, half = (bb.y1 - bb.y0) / 2;
    const zR = zAt(0, half), zE = zAt(half + OV, half);
    const xA = bb.x0 - OV, xB = bb.x1 + OV;
    out.push(slope(`${label} (south slope)`, [[xA, midY], [xB, midY], [xB, bb.y1 + OV], [xA, bb.y1 + OV]], [zR, zR, zE, zE]));
    out.push(slope(`${label} (north slope)`, [[xA, bb.y0 - OV], [xB, bb.y0 - OV], [xB, midY], [xA, midY]], [zE, zE, zR, zR]));
  } else {
    const midX = (bb.x0 + bb.x1) / 2, half = (bb.x1 - bb.x0) / 2;
    const zR = zAt(0, half), zE = zAt(half + OV, half);
    const yA = bb.y0 - OV, yB = bb.y1 + OV;
    out.push(slope(`${label} (east slope)`, [[midX, yA], [bb.x1 + OV, yA], [bb.x1 + OV, yB], [midX, yB]], [zR, zE, zE, zR]));
    out.push(slope(`${label} (west slope)`, [[bb.x0 - OV, yA], [midX, yA], [midX, yB], [bb.x0 - OV, yB]], [zE, zR, zR, zE]));
  }
  return out;
}

function extrudeTree(t: PlanTree): EngineObject {
  const acc: MeshAcc = { positions: [], triangles: [] };
  // Trunk: 12-sided cylinder, r 6", to 8'
  const R_TRUNK = 6, TRUNK_H = 96, SEG = 12;
  const ring = (r: number, zIn: number) => {
    const idx: number[] = [];
    for (let i = 0; i < SEG; i++) {
      const a = (i / SEG) * Math.PI * 2;
      idx.push(acc.positions.length / 3);
      const [x, y, z] = P(t.x + Math.cos(a) * r, t.y + Math.sin(a) * r, zIn);
      acc.positions.push(x, y, z);
    }
    return idx;
  };
  const bot = ring(R_TRUNK, 0), top = ring(R_TRUNK, TRUNK_H);
  for (let i = 0; i < SEG; i++) {
    const j = (i + 1) % SEG;
    acc.triangles.push(bot[i], bot[j], top[j], bot[i], top[j], top[i]);
  }
  // Canopy: lat/long sphere r = canopy/2 centred at 10'
  const R = Math.max(24, t.canopy / 2), CZ = 120, LAT = 6, LON = 10;
  const sph: number[][] = [];
  for (let la = 0; la <= LAT; la++) {
    const row: number[] = [];
    const phi = (la / LAT) * Math.PI;
    for (let lo = 0; lo < LON; lo++) {
      const th = (lo / LON) * Math.PI * 2;
      row.push(acc.positions.length / 3);
      const [x, y, z] = P(t.x + Math.sin(phi) * Math.cos(th) * R, t.y + Math.sin(phi) * Math.sin(th) * R, CZ + Math.cos(phi) * R);
      acc.positions.push(x, y, z);
    }
    sph.push(row);
  }
  for (let la = 0; la < LAT; la++) {
    for (let lo = 0; lo < LON; lo++) {
      const lo2 = (lo + 1) % LON;
      acc.triangles.push(sph[la][lo], sph[la][lo2], sph[la + 1][lo2], sph[la][lo], sph[la + 1][lo2], sph[la + 1][lo]);
    }
  }
  return { type: 'Site', name: `Tree (${Math.round(t.canopy / 12)}' canopy)`, meshes: [
    { color: TRUNK_COLOR, positions: acc.positions.slice(0, SEG * 2 * 3), triangles: acc.triangles.slice(0, SEG * 6) },
    { color: CANOPY_COLOR, positions: acc.positions.slice(SEG * 2 * 3), triangles: acc.triangles.slice(SEG * 6).map(i => i - SEG * 2) },
  ] };
}

/** Extrude the whole plan. Level i's floor sits on Σ prior heights + 12" floor depth. */
export function planTo3d(levels: PlanLevel3D[]): EngineObject[] {
  const out: EngineObject[] = [];
  let baseZ = 0;
  levels.forEach((level, i) => {
    const H = Math.max(24, Math.min(240, level.heightIn ?? DEFAULT_WALL_HEIGHT_IN));
    if (i > 0) {
      // Floor slab over this level's wall bbox (fallback: the level below's)
      const bb = wallBbox(level.walls) ?? wallBbox(levels[i - 1].walls);
      if (bb) {
        const acc: MeshAcc = { positions: [], triangles: [] };
        pushPrism(acc, [[bb.x0, bb.y0], [bb.x1, bb.y0], [bb.x1, bb.y1], [bb.x0, bb.y1]], baseZ, baseZ + FLOOR_THICK_IN);
        out.push(obj('Floor', `${level.name} — floor (12" deep)`, FLOOR_COLOR, acc));
      }
      baseZ += FLOOR_THICK_IN;
    }
    for (const w of level.walls) {
      const o = extrudeWall(level, w, baseZ, baseZ + H);
      if (o) out.push(o);
    }
    out.push(...extrudeRoof(level, baseZ + H));
    // Site elements always sit at ground
    for (const s of level.structures ?? []) {
      const acc: MeshAcc = { positions: [], triangles: [] };
      pushPrism(acc, [[s.x, s.y], [s.x + s.w, s.y], [s.x + s.w, s.y + s.h], [s.x, s.y + s.h]], 0, STRUCTURE_HEIGHT_IN);
      out.push(obj('Site', s.label || 'Existing structure', STRUCT_COLOR, acc));
    }
    for (const t of level.trees ?? []) out.push(extrudeTree(t));
    baseZ += H;
  });
  return out;
}
